/**
 * Card Component
 */

import * as playerActions from '../../../../redux/actions/player';
import * as actions from '../../../../redux/actions/ui';
import { makeIsTrackListened, makeIsTrackPlaying } from '../../../../redux/selectors/player';
import { makeIsTrackInProjects } from '../../../../redux/selectors/projects';
import { trackDetailsPanelIsVisible } from '../../../../redux/selectors/ui';
import { capitalizeString } from '../../../../utils/string';
import { getDefaultTrackAsset, getTrackAssets } from '../../../../utils/track';
import './styles.scss';

import * as classNames from 'classnames';
import { clamp, find } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { findDOMNode } from 'react-dom';
import ReactHoverObserver from 'react-hover-observer';
import { connect, MapStateToProps } from 'react-redux';
import * as Velocity from 'velocity-animate';
import { VelocityComponent } from 'velocity-react';

import { ActionSource } from '../../../../redux/actions/analytics';
import AddToProjectButton from '../../../atoms/buttons/AddToProjectButton';
import Button, { ButtonProps } from '../../../atoms/buttons/Button';
import DownloadButton from '../../../atoms/buttons/DownloadButton';
import { FindSimilarButton } from '../../../atoms/buttons/FindSimilarButton';
import {
  IconDownload,
  IconEdit,
  IconMore,
  IconMusicNote,
  IconProject,
  IconProjectSelected,
  IconTrash,
} from '../../../atoms/images/Icon';
import Thumbnail from '../../../atoms/images/Thumbnail';
import Heading, { HeadingProps } from '../../../atoms/text/Heading';
import { Span } from '../../../atoms/text/Label';
import { baseline } from '../../../styles/grid';
import DropdownMenu from '../../blocks/DropdownMenu';
import ThumbnailWithPlayButton from '../../buttons/ThumbnailWithPlayButton';
import { makeIsCardSelected } from './selectors';

interface IOwnStateProps {
  isInProjects: boolean;
  isPlaying: boolean;
  isSelected: boolean;
  listened: boolean;
  trackDetailsIsVisible: boolean;
}

interface IOwnDispatchProps {
  play(): void;
  pause(): void;
  hideDetails(): void;
  showDetails(): void;
}

interface IHoverObserverProps {
  isHovering: boolean;
}

interface IOwnProps {
  alwaysDisplayOptions?: boolean;
  backgroundColor?: Color;
  className?: string;
  description?: string;
  image?: string | IResponsiveImage;
  imageType?: 'square' | 'circle';
  license?: ILicenseUsage[];
  options?: Array<CardOption | ICardOption>;
  PriceElement?: React.ReactElement<ButtonProps>;
  size?: CardSize;
  subtitle: string;
  title: string;
  trackId?: string;
  trackResult?: GQL.ITrackSearchResult;
  showListenedStatus?: boolean;
  actionSource: ActionSource;
  onClick?(): any;
}

interface IOwnState {
  downloadMenuOpen: boolean;
  isAnimatingOverflow: boolean;
  isShrinked: boolean;
}

export type CardOwnProps = IOwnProps;
type CardProps = IOwnProps & IHoverObserverProps & IOwnStateProps & IOwnDispatchProps;

const classes = new BEM('card');
const defaultList = [];

class Card extends React.Component<CardProps, IOwnState> {
  static defaultProps: Partial<CardProps> = {
    imageType: 'square',
    options: ['PriceElement', 'AddToProject', 'ShowDetails', 'FindSimilar'],
    size: 'small',
    showListenedStatus: true,
  };

  private card: Element;
  private content: Element;
  private options: Element;
  private title: Element;
  private titleLabel: HTMLElement;
  private checkForTitleOverflowTimer: number | null;

  constructor(props: CardProps) {
    super(props);

    this.state = {
      downloadMenuOpen: false,
      isAnimatingOverflow: false,
      isShrinked: false,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Very fragile and unclear logic was commented out (see below)
    return true;
    // // Avoid updating the components uselessly when random mock data gets updated
    // for (const key in nextProps) {
    //   if (['image', 'description', 'play', 'pause', 'hideDetails', 'showDetails'].indexOf(String(key)) === -1) {
    //     if (!isEqual(nextProps[key], this.props[key])) {
    //       return true;
    //     }
    //   }
    // }
    // return !isEqual(nextState, this.state);
  }

  componentDidUpdate(prevProps: CardProps, prevState: IOwnState) {
    const mouseEnter = !prevProps.isHovering && this.props.isHovering;
    const mouseLeave = prevProps.isHovering && !this.props.isHovering;

    if (mouseEnter || (this.props.isHovering && this.state.isShrinked !== prevState.isShrinked)) {
      this.checkForTitleOverflowTimer = window.setTimeout(() => {
        this.checkForTitleOverflowTimer = null;
        this.checkForTitleOverflow();
      }, 500);
    } else if (mouseLeave) {
      if (this.state.isAnimatingOverflow) {
        this.setState({
          isAnimatingOverflow: false,
        });
      } else {
        window.clearTimeout(this.checkForTitleOverflowTimer!);
        this.checkForTitleOverflowTimer = null;
      }
    } else {
      // If is animating title overflow
      if (!prevState.isAnimatingOverflow && this.state.isAnimatingOverflow) {
        this.animateTitleOverflow();
      } else if (prevState.isAnimatingOverflow && !this.state.isAnimatingOverflow) {
        Velocity(this.titleLabel, 'stop');
        this.titleLabel.removeAttribute('style');
      }

      const cardWidth = this.card.getBoundingClientRect().width;
      if (!this.state.isShrinked && cardWidth < 300) {
        this.setState({ isShrinked: true });
      } else if (this.state.isShrinked && cardWidth >= 300) {
        this.setState({ isShrinked: false });
      }
    }
  }

  componentWillUnmount() {
    this.checkForTitleOverflowTimer = null;
  }

  onClickContent: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const {
      hideDetails,
      isSelected,
      onClick,
      showDetails,
      trackId,
    } = this.props;

    if (trackId) {
      isSelected ? hideDetails() : showDetails();
    } else if (onClick) {
      onClick();
    }
  }

  // Unused. Why?
  onClickPlayButton = () => {
    this.props.isPlaying ?
      this.props.pause()
      : this.props.play();
  }

  onClickMoreButton = () => {
    this.props.showDetails();
  }

  onClickCardOptionProxy = (func?: () => any) => (e: React.MouseEvent<any>) => {
    e.stopPropagation();

    if (func) {
      func();
    }
  }

  onToggleDropdownMenu = (downloadMenuOpen: boolean) => {
    this.setState({ downloadMenuOpen });
  }

  getCardTitle() {
    const {
      size,
      title,
    } = this.props;

    let titleHeading: HeadingType;

    switch (size) {
      case 'medium':
        titleHeading = 'h4';
        break;
      default:
        titleHeading = 'h5';
        break;
    }

    const props: HeadingProps = {
      is: titleHeading,
      text: title,
      ...classes('title'),
    };

    return (
      <Heading
        ref={(node) => this.title = findDOMNode(node!) as Element}
        {...props}
      />
    );
  }

  getCardSubtitle() {
    const {
      size,
      subtitle,
    } = this.props;
    let subtitleSize: LabelSize;
    switch (size) {
      case 'xlarge':
        subtitleSize = 'medium';
        break;
      default:
        subtitleSize = size!;
        break;
    }
    return (
      <Span
        text={subtitle}
        size={subtitleSize}
        textColor="bw7"
        {...classes('subtitle')}
      />
    );
  }

  getCardPriceElement() {
    const {
      PriceElement,
    } = this.props;

    return PriceElement ? React.cloneElement<ButtonProps>(PriceElement, {
      ...PriceElement.props,
      className: classNames(PriceElement.props.className, 'card__price-element'),
    }) : null;
  }

  getCardDescription() {
    const {
      description,
      size,
    } = this.props;

    if (size === 'small' && this.state.isShrinked) {
      return;
    }

    const descriptionSize = size === 'medium' ? 'large' : 'medium';
    return description && (
      <Span
        text={description}
        variant="description"
        size={descriptionSize}
        textColor="bw6"
        {...classes('description')}
      />
    );
  }

  getCardOptions() {
    const {
      actionSource,
      options,
      trackResult,
    } = this.props;

    const buttonProps: Partial<ButtonProps> = {
      variant: 'icon',
      size: 'small',
      className: 'card__option',
    };

    const downloadableAsset = getDefaultTrackAsset(trackResult!.assets!);
    const versions = getTrackAssets({
      assets: trackResult!.assets || defaultList,
      format: 'mp3',
      type: 'track-preview',
    });

    const optionPriceElement = (options as CardOption[])!.indexOf('PriceElement') > -1;
    const optionAddToProject = (options as CardOption[])!.indexOf('AddToProject') > -1;
    const optionShowDetails = (options as CardOption[])!.indexOf('ShowDetails') > -1;
    const optionDownload = (options as CardOption[])!.indexOf('Download') > -1;
    const optionFindSimilar = (options as CardOption[])!.indexOf('FindSimilar') > -1;
    const optionEditLicense = find((options as ICardOption[])!, { option: 'EditLicense' });
    const optionRemoveFromCart = find((options as ICardOption[])!, { option: 'RemoveFromCart' });
    const optionRemoveFromProject = find((options as ICardOption[])!, { option: 'RemoveFromProject' });

    return (
      <div key="card-options" {...classes('options')}>
        {optionAddToProject && (
          <div>
            {versions.length > 1 ? (
              <DropdownMenu
                align="left"
                Button={(
                  <Button
                    icon={this.props.isInProjects ? <IconProjectSelected /> : <IconProject />}
                    {...buttonProps}
                    className="card__option card__add-to-project"
                  />
                )}
                options={versions.map((asset) => (
                  <AddToProjectButton
                    key={`save-${asset.version}`}
                    showIcon={false}
                    trackId={trackResult!.id}
                    trackVersion={asset.version!}
                    label={capitalizeString(asset.version!)}
                    onClick={this.onClickCardOptionProxy()}
                    {...buttonProps}
                  />
                ))}
                requestClose={this.state.downloadMenuOpen === false || !this.props.isHovering}
                disableOnClickOutside={this.state.downloadMenuOpen === false}
                onToggle={this.onToggleDropdownMenu}
              />
            ) : (
              <AddToProjectButton
                label=""
                trackId={trackResult!.id}
                onClick={this.onClickCardOptionProxy()}
                {...buttonProps}
                className="card__option card__add-to-project"
              />
              )}
          </div>
        )}
        {optionShowDetails && (
          <Button
            title="More Info"
            icon={<IconMore />}
            {...buttonProps}
          />
        )}
        {optionFindSimilar && (
          <FindSimilarButton
            actionSource={actionSource}
            track={this.props.trackResult!}
            colorScheme="black-outline"
            variant="pill"
            size="xsmall"
            className="card__option card__find-similar"
          />
        )}
        {optionEditLicense && (
          optionEditLicense.element ?
            React.cloneElement(optionEditLicense.element, {
              ...optionEditLicense.element.props,
              ...buttonProps,
          })
          : (
              <Button
                onClick={optionEditLicense.listener ?
                  this.onClickCardOptionProxy(optionEditLicense.listener)
                  : undefined
                }
                icon={<IconEdit />}
                {...buttonProps}
              />
            )
          )
        }
        {optionDownload && (
          <VelocityComponent animation="fadeIn" duration={300} runOnMount>
            {versions.length > 1 ? (
              <DropdownMenu
                align="right"
                Button={(
                  <Button icon={<IconDownload />} {...buttonProps} />
                )}
                options={versions.map((asset) => (
                  <DownloadButton
                    key={`download-${asset.version}`}
                    icon={<IconMusicNote />}
                    label={capitalizeString(asset.version!)}
                    assetPath={asset.path!}
                    stopPropagationOnClick
                    track={trackResult!}
                  />
                ))}
                requestClose={this.state.downloadMenuOpen === false || !this.props.isHovering}
                disableOnClickOutside={this.state.downloadMenuOpen === false}
                onToggle={this.onToggleDropdownMenu}
                className="card__option card__download"
              />
            ) : (
                <DownloadButton
                  assetPath={downloadableAsset.path!}
                  stopPropagationOnClick
                  track={trackResult!}
                  {...buttonProps}
                  className="card__option card__download"
                />
              )}
          </VelocityComponent>
        )}
        {optionRemoveFromCart && (
          <Button
            onClick={optionRemoveFromCart.listener ?
              this.onClickCardOptionProxy(optionRemoveFromCart.listener)
              : undefined
            }
            icon={<IconTrash />}
            dangerous
            {...buttonProps}
          />
        )}
        {optionRemoveFromProject && (
          <VelocityComponent animation="fadeIn" duration={250} runOnMount>
            <Button
              onClick={optionRemoveFromProject.listener ?
                this.onClickCardOptionProxy(optionRemoveFromProject.listener)
                : undefined
              }
              icon={<IconTrash />}
              dangerous
              {...buttonProps}
            />
          </VelocityComponent>
        )}
        {optionPriceElement && this.getCardPriceElement()}
      </div>
    );
  }

  getCardImage() {
    const {
      image,
      imageType,
      size,
      trackId,
      trackResult,
    } = this.props;

    const imageSrc = (typeof image === 'string') ?
      { src: image }
      : { responsiveAssets: image };

    return trackId ? (
      <ThumbnailWithPlayButton
        responsive
        shape={imageType!}
        size={size}
        trackId={trackId!}
        trackResult={trackResult}
        onClick={this.props.showDetails}
        withShadow
        {...classes('image')}
        {...imageSrc}
      />
    ) : (
        <Thumbnail
          size={size}
          onClick={this.props.showDetails}
          withShadow
          {...classes('image')}
          {...imageSrc}
        />
      );
  }

  getCardContent(side: 'left' | 'right') {
    const {
      alwaysDisplayOptions,
      isHovering,
      size,
    } = this.props;

    const showTitle = size === 'xlarge' ? !isHovering : true;

    if (side === 'left') {
      return (
        <div {...classes('content__left')}>
          {showTitle && this.getCardTitle()}
          {showTitle && this.getCardSubtitle()}
          {size === 'medium' && this.getCardDescription()}
        </div>
      );
    }

    // Right Side

    const contentRightProps = {
      ...classes('content__right', '', { hover: alwaysDisplayOptions || isHovering }),
      ref: (node) => this.options = findDOMNode(node!) as Element,
    };
    return (
      <div {...contentRightProps}>
        {this.getCardOptions()}
      </div>
    );
  }

  render() {
    const {
      backgroundColor,
      className,
      isHovering,
      isInProjects,
      isPlaying,
      isSelected,
      listened,
      options,
      size,
      showListenedStatus,
    } = this.props;

    const {
      isAnimatingOverflow,
    } = this.state;

    const classModifiers = {
      'hover': isHovering,
      'animating-overflow': isAnimatingOverflow,
      'is-playing': isPlaying,
      'is-selected': isSelected,
      'listened': !!listened && !!showListenedStatus,
      [size!]: true,
    };

    const extraClasses = classNames(
      backgroundColor && `with-bgcolor--${!isPlaying && listened && showListenedStatus ? 'bw3' : backgroundColor}`,
      className,
    );

    const hasAddToProjectOption = options!.indexOf('AddToProject') > -1;
    const shouldDisplayInProjectIndicator = isInProjects && hasAddToProjectOption && !isHovering;

    return (
      <div
        {...classes('', classModifiers, extraClasses)}
        ref={(node) => this.card = node!}
      >
        <div {...classes('background')} />
        <div {...classes('wrapper')}>
          {this.getCardImage()}
          <div {...classes('content')} ref={(node) => this.content = node!} onClick={this.onClickContent}>
            {this.getCardContent('left')}
            {this.getCardContent('right')}
          </div>
        </div>
        {shouldDisplayInProjectIndicator && (
          <IconProjectSelected {...classes('in-project-indicator')} />
        )}
      </div>
    );
  }

  private checkForTitleOverflow() {
    if (this.title) {
      if (this.getTitleTextOverflow() > 0) {
        this.setState({
          isAnimatingOverflow: true,
        });
      }
    }
  }

  private animateTitleOverflow(loop?: boolean) {
    const translation = this.getTitleTextOverflow() + 10;
    const duration = clamp((translation / 100) * 3500, 0, 2000);
    const target = this.getTitleLabel();

    Velocity.RunSequence([
      {
        e: target,
        p: { translateX: [-translation, 0] },
        o: { delay: loop ? 1000 : 250, duration, easing: 'easeInOutSine' },
      },
      {
        e: target,
        p: { translateX: 0 },
        o: {
          delay: 250,
          duration,
          easing: 'easeInOutSine',
          complete: () => {
            if (this.props.isHovering) {
              this.animateTitleOverflow(true);
            }
          },
        },
      },
    ]);
  }

  private getTitleLabel() {
    if (!this.titleLabel) {
      this.titleLabel = this.title.getElementsByClassName('label')[0] as HTMLElement;
    }
    return this.titleLabel;
  }

  private getTitleTextOverflow() {
    const titleLabel = this.getTitleLabel();

    if (!this.title) {
      return 0;
    }

    const contentWidth = this.content.getBoundingClientRect().width;
    const labelWidth = titleLabel.getBoundingClientRect().width;

    let optionsWidth = baseline * 2;  // padding for gradient
    if (this.options) {
      optionsWidth += this.options.getBoundingClientRect().width;
    }

    return labelWidth - (contentWidth - optionsWidth);
  }
}

const makeMapStateToProps = () => {
  const isTrackPlaying = makeIsTrackPlaying();
  const isCardSelected = makeIsCardSelected();
  const isTrackInProject = makeIsTrackInProjects(false);
  const isTrackListened = makeIsTrackListened();

  const mapStateToProps: MapStateToProps<IOwnStateProps, IOwnProps, IStoreState> = (state, props) => ({
    isInProjects: isTrackInProject(state, props),
    isPlaying: isTrackPlaying(state, props),
    isSelected: isCardSelected(state, props),
    listened: isTrackListened(state, props),
    trackDetailsIsVisible: trackDetailsPanelIsVisible(state),
  });

  return mapStateToProps;
};

const ConnectedCard = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, IStoreState>(
  makeMapStateToProps,
  (dispatch, { license, trackId, trackResult }) => ({
    play: () => dispatch(playerActions.playTrackFromQueue({ trackId: trackId! })),
    pause: () => dispatch(playerActions.pause()),
    hideDetails: () => dispatch(actions.toggleTrackDetailsVisibility(false)),
    showDetails: () => dispatch(actions.showTrackDetails({ license, trackInfo: trackResult! })),
  }),
)(Card);

const CardWithHoverObserver = ({ className, ...cardProps }: IOwnProps) => (
  <ReactHoverObserver hoverDelayInMs={16} className={className}>
    {({ isHovering }) => (
      <ConnectedCard {...cardProps} isHovering={isHovering} />
    )}
  </ReactHoverObserver>
);

export default CardWithHoverObserver;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/molecules/media/Card/index.tsx